import { logsListOptions, ReturnLogsReadable } from '@crm/api'
import { LogsFilter } from '@crm/features/logs/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import {
  ConditionalText,
  DateTimeCell,
  TableContainer,
  TitleSection
} from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getFullName } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Tag } from 'antd'
import { useMemo } from 'react'

import type { TableColumnsType } from 'antd'

const actionColor: Record<string, string> = {
  created: 'green',
  updated: 'blue',
  completed: 'cyan',
  deleted: 'red',
  expired: 'orange'
}

const getEntity = (log: ReturnLogsReadable) =>
  log?.lead?.name ||
  log?.task?.title ||
  (log?.opportunity?.id ? `#${log.opportunity.id}` : '') ||
  getFullName(log?.contact)

export const Logs = () => {
  const { t } = useLocales()
  const { params } = useQueryUpdate()
  const sorter = useTableSorter()

  const { data, isLoading } = useQuery(
    logsListOptions({
      query: {
        ...params
      }
    })
  )

  const columns: TableColumnsType<ReturnLogsReadable> = useMemo(
    () => [
      {
        title: t('logs.who'),
        key: 'created_by',
        sorter: true,
        render: ({ created_by }) => (
          <ConditionalText condition={!!getFullName(created_by)}>
            {getFullName(created_by)}
          </ConditionalText>
        )
      },
      {
        title: t('logs.date_time'),
        key: 'created_at',
        sorter: true,
        render: ({ created_at }) => <DateTimeCell date={created_at} />
      },
      {
        title: t('logs.type'),
        key: 'type',
        sorter: true,
        render: ({ type }) =>
          type ? <Tag>{t(`logs.${type}`, type)}</Tag> : '-'
      },
      {
        title: t('logs.entity'),
        key: 'entity',
        render: (log: ReturnLogsReadable) => (
          <ConditionalText
            condition={!!getEntity(log)}
            className="table-column-max-width"
            isEllipsis
          >
            {getEntity(log)}
          </ConditionalText>
        )
      },
      {
        title: t('logs.action'),
        key: 'action',
        sorter: true,
        render: ({ action }) =>
          action ? (
            <Tag color={actionColor[action] ?? 'default'}>
              {t(`logs.${action}`, action)}
            </Tag>
          ) : (
            '-'
          )
      },
      {
        title: t('logs.result'),
        key: 'description',
        render: ({ description, full_description }) => (
          <ConditionalText
            condition={!!(full_description || description)}
            className="table-column-max-width"
            isEllipsis
          >
            {full_description || description}
          </ConditionalText>
        )
      }
    ],
    [t]
  )

  return (
    <>
      <TitleSection
        title={t('menu.logs')}
        count={data?.count}
        isLoading={isLoading}
      />

      <TableContainer
        rowKey="id"
        data={data}
        isHoverAlternative
        showSearch
        showSorterTooltip={false}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        leftChildren={<LogsFilter />}
      />
    </>
  )
}
